import { Component, OnInit } from "@angular/core";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import {
  AppBarAction,
  AppBarActionsService,
  AppBarTitleService,
  AppPageService,
  ButtonColor,
  SimpleDialogComponent,
  SimpleDialogData,
  FormHelper,
  MessageService,
  parseNumber,
  CommonActionKey,
} from "common";
import { readOnly, readWrite } from "projects/backoffice/src/app/user/user-permission/user-permission.data";
import { UserPermissionService } from "projects/backoffice/src/app/user/user-permission/user-permission.service";
import { ActivatedRoute } from "@angular/router";
import { AccountFacilityDetailsFacade } from "./account-facility-details.facade";
import { map, switchMap } from "rxjs/operators";
import { forkJoin, Observable, of } from "rxjs";
import { UntypedFormGroup } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { FacilitiesFacade } from "../../feature-modules/facility/domain/+state/facilities-facade";
import { FacilitiesActiveLabelValues, FacilityListData, FacilityQueryResult } from "../../feature-modules/facility/domain/models/facility.model";
import { AccountFacilityDetailsInputData } from "./account-facility-details.model";
import { AccountData, LoanFacilityData } from "../account.model";

@UntilDestroy()
@Component({
    selector: "ifb-account-facility-details",
    templateUrl: "./account-facility-details.component.html",
    styleUrls: ["./account-facility-details.component.scss"],
    providers: [AccountFacilityDetailsFacade],
    standalone: false
})
export class AccountFacilityDetailsComponent implements OnInit {
  readonly FacilitiesActiveLabelValues = FacilitiesActiveLabelValues;

  data: AccountFacilityDetailsInputData = {
      dataSource: undefined,
      accountData: undefined,
      facilitiesOptions: undefined,
      adminWriteAllowed: false,
      facilitiesReadAllowed: false,
      accountFacilityWriteAllowed: false,
      isUnpledged: true,
    };

  form: UntypedFormGroup;

  constructor(
    private readonly route: ActivatedRoute,
    private readonly dialog: MatDialog,
    private readonly facilitiesFacade: FacilitiesFacade,
    private readonly accountFacilityDetailsFacade: AccountFacilityDetailsFacade,
    private readonly userPermissionService: UserPermissionService,
    private readonly messageService: MessageService,
    private readonly appPageService: AppPageService,
    private readonly appBarActionsService: AppBarActionsService,
    private readonly appBarTitleService: AppBarTitleService
  ) {
    this.initForm();
    this.initAppActions();
  }

  ngOnInit(): void {
    this.initData();
    this.watchAppBarActions();
  }

  getDisabledOptionTooltip(option: FacilityListData): string {
    const amount = this.data?.accountData?.offer?.amount || 0;
    if (!option?.isDefault && option?.maximumAcceptableLoanCreditLimit < amount) {
      return 'Beyond maximum credit limit';
    }
    return '';
  }

  actionDispatch(action: AppBarAction) : void {
    const actionHandler: (action: AppBarAction) => void =
      this[action.id].bind(this);
    actionHandler(action);
  }

  cancel() : void {
    this.appPageService.back();
  }

  save() : void {
    FormHelper.showInvalidFormFields(this.form);
    if (!this.form.valid) return;

    SimpleDialogComponent.show(this.dialog, this.getSaveConfirmationDialogData()).subscribe((actionKey: string) => {
      if(actionKey === CommonActionKey.Confirm) {
        this.accountFacilityDetailsFacade.patchLoanFacility(this.data?.accountData?.id, this.form).subscribe({
          next: () => this.handleSuccess(),
          error: (err) => this.handleError(err),
        });
      }
    });
  }

  private initForm() : void {
    this.form = this.accountFacilityDetailsFacade.initForm();
  }

  private initData() : void {
    this.appBarTitleService.title = "Facility";

    forkJoin([this.initAdminFacilitiesPermissions(), this.initReadFacilitiesPermissions(), this.initAccountFacilitiesPermissions(), this.initRouteParams()])
      .pipe(untilDestroyed(this), switchMap(([adminFacilitiesPermissions, readFacilitiesPermissions, accountFacilitiesPermissions, params]) =>
        this.initFacilitiesOptions(params.accountData, readFacilitiesPermissions).pipe(
          map((facilitiesOptions) => ({
            adminFacilitiesPermissions,
            readFacilitiesPermissions,
            accountFacilitiesPermissions,
            params,
            facilitiesOptions
          }))
        )
      ))
      .subscribe(
        ({ adminFacilitiesPermissions, readFacilitiesPermissions, accountFacilitiesPermissions, params, facilitiesOptions }) => {

          this.data = {
            dataSource: params.facilityInfo,
            accountData: params.accountData,
            facilitiesOptions: facilitiesOptions,
            isUnpledged: params.isUnpledged,
            adminWriteAllowed: adminFacilitiesPermissions,
            facilitiesReadAllowed: readFacilitiesPermissions,
            accountFacilityWriteAllowed: accountFacilitiesPermissions
          };

          if (this.data.dataSource) {
            this.accountFacilityDetailsFacade.patchFormFromFacilityInfo(this.form, this.data.facilitiesOptions.values, this.data.dataSource?.currentFacility);
          }
          if (!this.data.accountFacilityWriteAllowed || (!this.data.isUnpledged && !this.data.adminWriteAllowed)) {
            this.form.disable({ emitEvent: false });
          }
        }
      );
  }

  private initAdminFacilitiesPermissions() : Observable<boolean> {
    return this.userPermissionService
      .granted([readWrite("admin-facilities")])
      .pipe(untilDestroyed(this));
  }

  private initReadFacilitiesPermissions() : Observable<boolean> {
    return this.userPermissionService
      .granted([readOnly("admin-facilities")])
      .pipe(untilDestroyed(this));
  }

  private initAccountFacilitiesPermissions() : Observable<boolean> {
    return this.userPermissionService
      .granted([readWrite("servicing-account-facilities")])
      .pipe(untilDestroyed(this));
  }

  private initFacilitiesOptions(accountData: AccountData, canReadFacilties: boolean): Observable<FacilityQueryResult> {
    return canReadFacilties ?
      this.facilitiesFacade.getFacilities({ skip: 0, limit: 60, active: true, productCode: accountData?.productCode }).pipe(untilDestroyed(this))
      : of({
        values: [] as FacilityListData[],
        totalCount: 0
      });
  }

  private initRouteParams(): Observable<{ facilityInfo: LoanFacilityData, accountData: AccountData, isUnpledged: boolean }> {
    const id = parseNumber(this.route.snapshot.paramMap.get("id"));
    const uniqueIdRaw = this.route.snapshot.paramMap.get("uniqueId");

    return this.accountFacilityDetailsFacade.getAccountData(id, uniqueIdRaw).pipe(
      untilDestroyed(this),
      map((accountData) => {
        return {
          facilityInfo: accountData?.facility,
          accountData: accountData,
          isUnpledged: accountData?.facility?.currentFacility?.isDefault ?? true
        };
      })
    );
  }

  private initAppActions() : void {
    this.accountFacilityDetailsFacade.initAppBarActions();
  }

  private watchAppBarActions(): void {
    this.appBarActionsService.invoking
      .pipe(untilDestroyed(this))
      .subscribe(this.actionDispatch.bind(this));

      this.form.statusChanges
      .pipe(untilDestroyed(this))
      .subscribe((newStatus: string) => {

        const isEnabled =
          newStatus === "VALID" &&
          this.form.dirty &&
          this.data.accountFacilityWriteAllowed;
        this.appBarActionsService.enable("save", isEnabled);
      });
  }

  private handleSuccess() : void {
    const message = this.data.isUnpledged
      ? "Loan has been successfully pledged."
      : "Loan has been successfully reassigned.";
    this.messageService.success(message);
    this.appPageService.back();
  }

  private handleError(err) : void {
    const message = this.data.isUnpledged
      ? `Loan cannot be pledged. ${err.error?.message ?? ''}`
      : `Loan cannot be reassigned. ${err.error?.message ?? ''}`;
    this.messageService.error(message);
  }

  private getSaveConfirmationDialogData() : SimpleDialogData {
    return {
      body: this.data?.isUnpledged ? `Do you want to pledge loan to ${this.getSelectedFacilityName()} facility?` : `Do you want to reassign loan from ${this.data?.dataSource?.currentFacility?.name} facility to ${this.getSelectedFacilityName()}`,
      actions: [
        { label: "CANCEL", buttonColor: ButtonColor.Default, actionKey: CommonActionKey.Cancel },
        { label: "CONFIRM", buttonColor: ButtonColor.Primary, actionKey: CommonActionKey.Confirm },
      ],
    }
  }

  private getSelectedFacilityName(): string {
    return this.form.controls.facility.value?.name;
  }
}
